import { API_ROOT } from "../api_endpoints";
import { acquireTokenPopupAndCallAPI } from "../authentication";

const GOT_EW_WORKFLOWS = "GOT_EW_WORKFLOWS";
const GOT_EW_WORKFLOW = "GOT_EW_WORKFLOW";
const GOT_EW_WORKFLOWS_BY_RECORD = "GOT_EW_WORKFLOWS_BY_RECORD";
const GOT_EW_ACTIVE_TASKS = "GOT_EW_ACTIVE_TASKS";

const gotEWWorkflows = (workflows) => ({
  type: GOT_EW_WORKFLOWS,
  workflows,
});

const gotEWWorkflow = (workflow) => ({
  type: GOT_EW_WORKFLOW,
  workflow,
});

const gotEWWorkflowsByRecord = (workflows) => ({
  type: GOT_EW_WORKFLOWS_BY_RECORD,
  workflows,
});

const gotEWActiveTasks = (activeTasks) => ({
  type: GOT_EW_ACTIVE_TASKS,
  activeTasks,
});

const selectCurrentWorkflow = (workflows) => {
  const workflowList = Array.isArray(workflows) ? workflows : [];
  if (workflowList.length === 0) {
    return null;
  }

  const orderedWorkflows = [...workflowList].sort((left, right) => {
    const leftId = left && left.wf_instance ? left.wf_instance.id || 0 : 0;
    const rightId = right && right.wf_instance ? right.wf_instance.id || 0 : 0;
    return rightId - leftId;
  });

  return orderedWorkflows.find((workflow) => (
    workflow &&
    workflow.wf_instance &&
    workflow.wf_instance.status === 0
  )) || orderedWorkflows[0];
};

export const getAllEWWorkflows = () => async (dispatch) => {
  try {
    const response = await acquireTokenPopupAndCallAPI((token) =>
      fetch(API_ROOT.ewWorkflows, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    const workflows = await response.json();
    dispatch(gotEWWorkflows(workflows));
    return workflows;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getEWWorkflowById = (workflowId) => async (dispatch) => {
  try {
    const response = await acquireTokenPopupAndCallAPI((token) =>
      fetch(API_ROOT.ewWorkflowById(workflowId), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );

    if (!response.ok) {
      dispatch(gotEWWorkflow(null));
      return null;
    }

    const workflow = await response.json();
    dispatch(gotEWWorkflow(workflow));
    return workflow;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getEWWorkflowByRecord = (recordType, recordId) => async (dispatch) => {
  try {
    const response = await acquireTokenPopupAndCallAPI((token) =>
      fetch(API_ROOT.ewWorkflowByRecord(recordType, recordId), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );

    // 404 = no workflow exists for this record yet — expected, not an error
    if (response.status === 404) {
      dispatch(gotEWWorkflowsByRecord([]));
      dispatch(gotEWWorkflow(null));
      return null;
    }

    if (!response.ok) {
      console.error(`[getEWWorkflowByRecord] unexpected status ${response.status} for ${recordType}/${recordId}`);
      dispatch(gotEWWorkflowsByRecord([]));
      dispatch(gotEWWorkflow(null));
      return null;
    }

    const workflows = await response.json();
    const workflowList = Array.isArray(workflows) ? workflows : (workflows ? [workflows] : []);
    const workflow = selectCurrentWorkflow(workflowList);
    dispatch(gotEWWorkflowsByRecord(workflowList));
    dispatch(gotEWWorkflow(workflow));
    return workflow;
  } catch (error) {
    console.error(`[getEWWorkflowByRecord] failed for ${recordType}/${recordId}:`, error);
    dispatch(gotEWWorkflowsByRecord([]));
    dispatch(gotEWWorkflow(null));
    return null;
  }
};

export const initEWWorkflow = (workflowForm) => async (dispatch) => {
  try {
    const response = await acquireTokenPopupAndCallAPI((token) =>
      fetch(API_ROOT.initEWWorkflow, {
        method: "POST",
        body: JSON.stringify(workflowForm),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
    );

    if (!response.ok) {
      return null;
    }

    const workflowId = await response.json();
    if (workflowId) {
      await dispatch(getEWWorkflowById(workflowId));
    }
    return workflowId;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const completeEWWorkflowStep = (workflowStepForm) => async (dispatch) => {
  try {
    const response = await acquireTokenPopupAndCallAPI((token) =>
      fetch(API_ROOT.completeEWWorkflowStep, {
        method: "POST",
        body: JSON.stringify(workflowStepForm),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
    );

    if (!response.ok) {
      return null;
    }

    const stepId = await response.json();
    return stepId;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getMyActiveEWWorkflowTasks = () => async (dispatch) => {
  try {
    const response = await acquireTokenPopupAndCallAPI((token) =>
      fetch(API_ROOT.myActiveEWWorkflowTasks, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    const activeTasks = await response.json();
    dispatch(gotEWActiveTasks(activeTasks));
    return activeTasks;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getEWSeniorReviewers = () => async () => {
  try {
    const response = await acquireTokenPopupAndCallAPI((token) =>
      fetch(API_ROOT.ewWorkflowSeniorReviewers, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );

    if (!response.ok) {
      return [];
    }

    const reviewers = await response.json();
    return Array.isArray(reviewers) ? reviewers : [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

const initialState = {
  workflows: [],
  currentWorkflow: null,
  currentWorkflowsByRecord: [],
  activeTasks: [],
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case GOT_EW_WORKFLOWS: {
      const { workflows } = action;
      return { ...state, workflows };
    }
    case GOT_EW_WORKFLOW: {
      const { workflow } = action;
      return { ...state, currentWorkflow: workflow };
    }
    case GOT_EW_WORKFLOWS_BY_RECORD: {
      const { workflows } = action;
      return { ...state, currentWorkflowsByRecord: workflows };
    }
    case GOT_EW_ACTIVE_TASKS: {
      const { activeTasks } = action;
      return { ...state, activeTasks };
    }
    default:
      return state;
  }
}
